<template>
  <div v-loading="initialLoading">
    <div class="tw-relative tw-flex tw-justify-between tw-items-center tw-gap-2 tw-mb-8">
      <router-link
        to="/administration/internal/faq"
        class="tw-mb-3 sm:tw-mb-0"
      >
        <i-button-icon-text
          icon="icon-angle-left"
          size="medium"
        >
          {{ $t('adm.internal.faq.detail.back_button') }}
        </i-button-icon-text>
      </router-link>
      <h1 class="tw-absolute tw-top-1/2 tw-left-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2 tw-tracking-wider">
        {{ $t(isEdit ? 'adm.internal.faq.faq_dialog.title_edit' : 'adm.internal.faq.faq_dialog.title_new') }}
      </h1>
      <div class="tw-flex tw-gap-2">
        <i-confirm-popover
          v-if="isEdit"
          :cancel-text="$t('common.actions.cancel')"
          :submit-text="$t('common.actions.delete')"
          @submit="deleteFaq"
        >
          <template #reference />
          <i-button
            :is-loading="isLoading"
          >
            {{ $t('common.actions.delete') }}
          </i-button>
          <template #content>
            <div>
              {{ $t('adm.permits.faq.delete_dialog.confirm_text') }}
              {{ form.short_name }}
            </div>
          </template>
        </i-confirm-popover>
        <i-button
          type="primary"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </div>

    <el-form
      ref="Form"
      :model="form"
      :rules="formRules"
      label-position="top"
      :disabled="isLoading"
      class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
      @submit.native.prevent="submitForm"
    >
      <div class="tw-flex tw-items-start">
        <i-form-item
          :label="$t('common.fields.name_en')"
          :error="errors?.name_en?.[0]"
          :class="form.name_en ? 'has-value' : ''"
          prop="name_en"
        >
          <el-input
            v-model="form.name_en"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :label="$t('common.fields.name_ru')"
          :error="errors?.name_ru?.[0]"
          :class="form.name_ru ? 'has-value' : ''"
          prop="name_ru"
        >
          <el-input
            v-model="form.name_ru"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-center tw-gap-2">
        <i-form-item
          :label="$t('common.fields.short_name')"
          :error="errors?.short_name?.[0]"
          :class="form.short_name ? 'has-value' : ''"
          prop="short_name"
          class="tw-flex-3"
        >
          <el-input
            v-model="form.short_name"
          />
        </i-form-item>
        <i-form-item
          :label="$t('common.fields.human_order')"
          :error="errors?.human_order?.[0]"
          :class="form.human_order ? 'has-value' : ''"
          prop="human_order"
          class="tw-flex-1"
        >
          <el-input
            v-model="form.human_order"
          />
        </i-form-item>
        <i-form-item
          prop="enabled"
          class="tw-flex-1"
        >
          <el-switch
            v-model="form.enabled"
            :active-text="$t('common.fields.active')"
          />
        </i-form-item>
      </div>
      <div class="tw-flex tw-justify-between tw-items-center tw-gap-2 tw-my-3">
        <h2 class="tw-tracking-wider">
          {{ $t('adm.internal.faq.faq_dialog.subtitle_questions') }}
        </h2>
        <i-button
          size="small"
          type="primary"
          @click="addQuestion"
        >
          {{ $t('adm.internal.faq.faq_dialog.question_new') }}
        </i-button>
      </div>
      <draggable
        v-model="form.queries"
        item-key="id"
        class="tw-flex tw-flex-col tw-gap-2"
      >
        <template
          #item="{element, index}"
        >
          <i-collapse-item
            :title="element.name_en || element.name_ru || `Question #${index + 1}`"
            :class="[
              element.hasError ? 'tw-border tw-border-primary' : '',
            ]"
          >
            <template #content>
              <div class="tw-px-4">
                <div class="tw-flex">
                  <div class="tw-flex tw-w-full tw-mr-2">
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-w-full">
                      <i-form-item
                        :label="$t('common.fields.question_en')"
                        :error="errors?.queries?.[index]?.name_en?.[0]"
                        :prop="`queries.${index}.name_en`"
                        :rules="formRules.queries[index].name_en"
                        :class="element.name_en ? 'has-value' : ''"
                        class="tw-flex-3"
                      >
                        <el-input
                          v-model="element.name_en"
                        />
                      </i-form-item>
                      <i-form-item
                        :label="$t('common.fields.answer_en')"
                        :error="errors?.queries?.[index]?.answer_en?.[0]"
                        :prop="`queries.${index}.answer_en`"
                        :rules="formRules.queries[index].answer_en"
                        :class="element.answer_en ? 'has-value' : ''"
                        class="tw-flex-3"
                      >
                        <el-input
                          v-model="element.answer_en"
                          type="textarea"
                          :rows="2"
                        />
                      </i-form-item>
                      <i-form-item
                        :label="$t('common.fields.name_ru')"
                        :error="errors?.queries?.[index]?.name_ru?.[0]"
                        :prop="`queries.${index}.name_ru`"
                        :rules="formRules.queries[index].name_ru"
                        :class="element.name_ru ? 'has-value' : ''"
                        class="tw-flex-3"
                      >
                        <el-input
                          v-model="element.name_ru"
                        />
                      </i-form-item>
                      <i-form-item
                        :prop="`queries.${index}.answer_ru`"
                        :error="errors?.queries?.[index]?.answer_ru?.[0]"
                        :rules="formRules.queries[index].answer_ru"
                        :class="element.answer_ru ? 'has-value' : ''"
                        class="tw-flex-3"
                      >
                        <el-input
                          v-model="element.answer_ru"
                          type="textarea"
                          :rows="2"
                          :placeholder="$t('common.fields.answer_ru')"
                        />
                      </i-form-item>
                    </div>
                  </div>
                  <div
                    class="d-flex flex-column justify-space-between"
                    style="gap: 8px"
                  >
                    <i-button
                      slot="reference"
                      type="white"
                      is-icon
                      class="tw-text-primary"
                      :disabled="form.queries.length === 1"
                      @click="deleteQuestion(index)"
                    >
                      <icon-trash />
                    </i-button>
                  </div>
                </div>
              </div>
            </template>
          </i-collapse-item>
        </template>
      </draggable>
    </el-form>
  </div>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { getValidationError } from '@/helpers';
import { nanoid } from 'nanoid';
import draggable from 'vuedraggable';

const FORM_BASE = {
  name_en: '', // required, maxLength: 255
  name_ru: '', // required, maxLength: 255
  short_name: '', // required maxLength: 128
  human_order: '1', // required, minimum - 1
  enabled: false, // required
  queries: [
    {
      id: nanoid(),
      name_en: '', // required, maxLength: 255
      name_ru: '', // required, maxLength: 255
      answer_en: '', // required
      answer_ru: '', // required
      hasError: false,
    },
  ],
};

export default {
  name: 'FaqNewOrEdit',
  components: { draggable },
  data() {
    return {
      initialLoading: false,
      isLoading: false,
      faq: null,
      errors: {},
      form: _cloneDeep(FORM_BASE),
      formRules: {
        name_en: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 255, message: 'Max 255 characters', trigger: 'blur' },
        ],
        name_ru: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 255, message: 'Max 255 characters', trigger: 'blur' },
        ],
        short_name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 128, message: 'Max 128 characters', trigger: 'blur' },
        ],
        human_order: [
          { required: true, message: 'Required', trigger: 'blur' },
          {
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (!isNaN(parseInt(value))) {
                callback();
              } else if(value < 1) {
                callback(new Error('Must be greater than zero'));
              } else {
                callback(new Error('Must be a number'));
              }
            },
          },
        ],
        enabled: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        queries: [
          this.queryFormRule(0),
        ],
      },
    };
  },
  computed: {
    isEdit() {
      return this.$route.name === 'internalFaqEdit';
    },
  },
  async mounted() {
    if (this.isEdit) {
      this.initialLoading =  true;
      const faq = await this.$store.dispatch('v2/admin/cms/internal/faq/getFaqDetails', {
        id: this.$route.params.id,
      });
      this.faq = faq;
      faq.queries.forEach((_, index) => {
        this.formRules.queries.push(this.queryFormRule(index));
      });
      this.form = {
        id: faq.id,
        name_en: faq.name_en,
        name_ru: faq.name_ru,
        short_name: faq.short_name,
        human_order: faq.human_order,
        enabled: faq.enabled,
        queries: faq.queries.map((query) => ({
          id: query.id,
          name_en: query.name_en,
          name_ru: query.name_ru,
          answer_en: query.answer_en,
          answer_ru: query.answer_ru,
          hasError: false,
        })),
      };
      this.initialLoading = false;
    }
  },

  methods: {
    queryFormRule(index) {
      const validator = (rule, value, callback) => {
        if (!value) {
          this.form.queries[index].hasError = true;
          callback(new Error('Required'));
        } else {
          this.form.queries[index].hasError = this.form.queries[index].hasError || false;
          callback();
        }
      };
      return {
        name_en: [
          {
            trigger: 'blur',
            validator,
          },
        ],
        name_ru: [
          {
            trigger: 'blur',
            validator,
          },
        ],
        answer_en: [
          {
            trigger: 'blur',
            validator,
          },
        ],
        answer_ru: [
          {
            trigger: 'blur',
            validator,
          },
        ],
      };
    },
    addQuestion() {
      this.form.queries.push({
        id: nanoid(),
        name_en: '',
        name_ru: '',
        answer_en: '',
        answer_ru: '',
        hasError: false,
      });
      this.formRules.queries.push(this.queryFormRule(this.form.queries.length - 1));
    },

    deleteQuestion(index) {
      this.form.queries.splice(index, 1);
      this.formRules.queries.splice(index, 1);
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        let response;
        if (this.isEdit) {
          response = await this.$store.dispatch('v2/admin/cms/internal/faq/editFaq', {
            faq: this.form,
            id: this.faq.id,
          });
        } else {
          response = await this.$store.dispatch('v2/admin/cms/internal/faq/addFaq', {
            payload: this.form,
          });
        }

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

      } catch (error) {
        this.errors = error.response.data.data;

        this.form.queries.forEach((_, index) => {
          if (this.errors.queries && this.errors.queries[index]) {
            this.form.queries[index].hasError = Object.keys(this.errors.queries[index]).length > 0;
          }
        });

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
    async deleteFaq() {
      this.isLoading = true;
      try {
        await this.$store.dispatch('v2/admin/cms/internal/faq/deleteFaq', this.faq.id);
        this.$router.push({ name: 'internalFaq' });
      } catch (e) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
